import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import type { FavoriteItem } from '../types/favorites';
import type { SupportedLanguages } from '../i18n';
import { logger } from '../lib/logger';
import { hotelConfig } from '../config/hotel.config';
import { slugify } from './slugify';
import { formatDate, formatPhoneNumber } from './formatters';
import { formatTravelTime } from './formatDuration';

// Function to strip HTML tags from text
function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces
    .replace(/&amp;/g, '&') // Replace &amp; with &
    .replace(/&lt;/g, '<') // Replace &lt; with <
    .replace(/&gt;/g, '>') // Replace &gt; with >
    .replace(/&quot;/g, '"') // Replace &quot; with "
    .replace(/&#39;/g, "'") // Replace &#39; with '
    .replace(/\s+/g, ' ') // Replace multiple spaces with a single space
    .trim(); // Trim whitespace
}

export async function generateFavoritesPDF(
  favorites: FavoriteItem[],
  config: any,
  translations: Record<string, Record<string, string>>,
  language: SupportedLanguages
): Promise<void> {
  try {
    // For Asian languages and Russian, use English as fallback
    const useFallbackLanguage = ['ja', 'zh', 'ru'].includes(language);
    const pdfLanguage = useFallbackLanguage ? 'en' : language;
    
    logger.debug('Starting PDF generation', {
      category: 'PDF',
      data: { 
        language,
        useFallbackLanguage,
        pdfLanguage,
        favoritesCount: favorites.length
      }
    });

    // Create a new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Configure fonts and colors
    const textColor = config['dark-color'];
    const accentColor = config['major-color'];
    const secondaryColor = config['medium-color'];
    
    // Page layout constants
    const margin = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (margin * 2);
    const imageWidth = 60;
    const imageHeight = 40;
    const itemSpacing = 15;
    const qrCodeSize = 25;
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Function to get translated text
    const getTranslatedText = (key: string): string => {
      const translation = translations[key];
      if (!translation) return key;
      
      // For Asian languages and Russian, use English as fallback
      if (useFallbackLanguage) {
        return translation['en'] || translation['fr'] || key;
      }
      
      return translation[language] || translation['fr'] || key;
    };

    // Function to get translated category/type
    const getTranslatedCategory = (category: string): string => {
      const translation = translations[category];
      if (!translation) return category;
      
      // For Asian languages and Russian, use English as fallback
      if (useFallbackLanguage) {
        return translation['en'] || translation['fr'] || category;
      }
      
      return translation[language] || translation['fr'] || category;
    };

    // Add header
    doc.setFontSize(20);
    doc.setTextColor(textColor);
    doc.text(config.name, margin, 20);

    // Add favorites title with count
    doc.setFontSize(16);
    const favoritesTitle = `${getTranslatedText('favorites')} (${favorites.length})`;
    doc.text(favoritesTitle, margin, 30);

    // Add date and time
    doc.setFontSize(10);
    const dateOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    
    // Format date in the appropriate language
    let dateStr;
    try {
      dateStr = new Date().toLocaleDateString(pdfLanguage, dateOptions);
    } catch (error) {
      // Fallback to English if there's an error with date formatting
      dateStr = new Date().toLocaleDateString('en', dateOptions);
    }
    
    doc.text(dateStr, margin, 35);

    // Add separator line
    doc.setDrawColor(accentColor);
    doc.setLineWidth(0.5);
    doc.line(margin, 40, pageWidth - margin, 40);

    // Initial position for content
    let yPosition = 50;

    // Function to check if we need a new page
    const checkNewPage = (height: number): boolean => {
      if (yPosition + height > pageHeight - margin) {
        doc.addPage();
        
        // Add header to new page
        doc.setFontSize(12);
        doc.setTextColor(textColor);
        doc.text(config.name, margin, 15);
        
        // Add separator line
        doc.setDrawColor(accentColor);
        doc.setLineWidth(0.3);
        doc.line(margin, 20, pageWidth - margin, 20);
        
        yPosition = 30;
        return true;
      }
      return false;
    };

    // Function to load an image
    const loadImage = (url: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = (e) => {
          logger.error('Error loading image for PDF', {
            category: 'PDF',
            data: { error: e, url }
          });
          reject(new Error(`Failed to load image: ${url}`));
        };
        img.src = url;
      });
    };

    // Function to add an image with error handling
    const addImage = async (imageUrl: string, x: number, y: number): Promise<void> => {
      try {
        if (!imageUrl) return;
        
        const img = await loadImage(imageUrl);
        const ratio = img.width / img.height;
        let finalWidth = imageWidth;
        let finalHeight = imageHeight;
        
        if (ratio > 1) {
          // Landscape image
          finalHeight = imageWidth / ratio;
        } else {
          // Portrait image
          finalWidth = imageHeight * ratio;
        }

        doc.addImage(img, 'JPEG', x, y, finalWidth, finalHeight);
      } catch (error) {
        logger.error('Error adding image to PDF', {
          category: 'PDF',
          data: { error, imageUrl }
        });
        // Add a placeholder for failed images
        doc.setFillColor(240, 240, 240);
        doc.rect(x, y, imageWidth, imageHeight, 'F');
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text('Image non disponible', x + imageWidth/2, y + imageHeight/2, { align: 'center' });
      }
    };

    // Function to add a button
    const addButton = (text: string, url: string, x: number, y: number, width: number): number => {
      const buttonHeight = 7;
      const buttonRadius = 2;
      
      // Draw button background
      doc.setFillColor(accentColor);
      doc.roundedRect(x, y - 5, width, buttonHeight, buttonRadius, buttonRadius, 'F');
      
      // Add button text - adjusted vertical position for better centering
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(text, x + width/2, y - 1, { align: 'center' }); // Adjusted y position for better vertical centering
      
      // Add link
      doc.link(x, y - 5, width, buttonHeight, { url });
      
      return buttonHeight + 2; // Return height + spacing
    };

    // Sort favorites: events with dates first (sorted by date), then others
    const sortedFavorites = [...favorites].sort((a, b) => {
      const aHasDate = (a.sourceType === 'event' || a.sourceType === 'event-api') && a.data.date;
      const bHasDate = (b.sourceType === 'event' || b.sourceType === 'event-api') && b.data.date;
      
      // If both have dates, sort by date
      if (aHasDate && bHasDate) {
        const dateA = new Date(a.data.date);
        const dateB = new Date(b.data.date);
        return dateA.getTime() - dateB.getTime();
      }
      
      // If only one has a date, it comes first
      if (aHasDate) return -1;
      if (bHasDate) return 1;
      
      // If neither has a date, sort by name
      return a.data.name.localeCompare(b.data.name);
    });

    // Process each favorite item
    for (const [index, favorite] of sortedFavorites.entries()) {
      const item = favorite.data;
      const itemHeight = 70; // Estimated height
      
      // Check if we need a new page
      checkNewPage(itemHeight + itemSpacing);
      
      // Get image to display (item image or replacement image)
      const displayImage = item.image || favorite.metadata.sourceSection.replace_img?.[0]?.url;
      
      // Get translated description if available and strip HTML
      let description = favorite.translations?.descriptions?.[language] || 
                       favorite.data.descriptions?.[language] || 
                       favorite.data.description;
      
      // For Asian languages and Russian, use English description as fallback
      if (useFallbackLanguage) {
        description = favorite.translations?.descriptions?.['en'] || 
                     favorite.data.descriptions?.['en'] || 
                     favorite.data.descriptions?.['fr'] || 
                     favorite.data.description;
      }
      
      // Strip HTML from description
      description = stripHtml(description);
      
      // Start Y position for this item
      const startY = yPosition;
      
      // Add image if available
      if (displayImage) {
        await addImage(displayImage, margin, yPosition);
      }
      
      // Add item name
      doc.setFontSize(14);
      doc.setTextColor(textColor);
      doc.text(item.name, margin + imageWidth + 5, yPosition + 7);
      
      // Initialize text position
      let textY = yPosition + 15;
      const lineHeight = 5;
      
      // Add category and type
      doc.setFontSize(9);
      doc.setTextColor(accentColor);
      const categoryText = getTranslatedCategory(item.category);
      const typeText = item.type ? ` - ${getTranslatedCategory(item.type)}` : '';
      doc.text(`${categoryText}${typeText}`, margin + imageWidth + 5, textY);
      textY += lineHeight;
      
      // Add address if available
      if (item.fulladress) {
        doc.setFontSize(9);
        doc.setTextColor(textColor);
        doc.text(item.fulladress, margin + imageWidth + 5, textY);
        textY += lineHeight;
      }
      
      // Add phone if available
      if (item.telephone) {
        doc.setFontSize(9);
        doc.setTextColor(textColor);
        doc.text(formatPhoneNumber(item.telephone), margin + imageWidth + 5, textY);
        textY += lineHeight;
      }
      
      // Add specific details based on favorite type
      switch (favorite.sourceType) {
        case 'event':
        case 'event-api':
          // Add date and time
          if (item.date) {
            doc.setFontSize(9);
            doc.setTextColor(secondaryColor);
            
            // Format date in the appropriate language
            let dateStr;
            try {
              const dateObj = new Date(item.date);
              dateStr = dateObj.toLocaleDateString(pdfLanguage, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });
            } catch (error) {
              // Fallback to English if there's an error with date formatting
              const dateObj = new Date(item.date);
              dateStr = dateObj.toLocaleDateString('en', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });
            }
            
            let timeStr = '';
            if (item.hour) {
              timeStr = ` - ${item.hour}`;
              if (item.end_time) {
                timeStr += ` → ${item.end_time}`;
              } else if (item.duration) {
                timeStr += ` (${formatTravelTime(item.duration, pdfLanguage)})`;
              }
            }
            
            doc.text(`${dateStr}${timeStr}`, margin + imageWidth + 5, textY);
            textY += lineHeight;
          }
          
          // Add price if available
          if (item.price) {
            doc.setFontSize(9);
            doc.setTextColor(accentColor);
            doc.text(`${getTranslatedText('price')}: ${item.price}`, margin + imageWidth + 5, textY);
            textY += lineHeight;
          }
          
          // Add place if available
          if (item.place) {
            doc.setFontSize(9);
            doc.setTextColor(textColor);
            doc.text(item.place, margin + imageWidth + 5, textY);
            textY += lineHeight;
          }
          break;
          
        case 'standard':
          // Add distance and travel time
          if (item.distance) {
            doc.setFontSize(9);
            doc.setTextColor(secondaryColor);
            
            let distanceText = `${item.distance} km`;
            if (item.timewalk) {
              distanceText += ` - ${formatTravelTime(item.timewalk, pdfLanguage)} ${getTranslatedText('walk')}`;
            }
            
            doc.text(distanceText, margin + imageWidth + 5, textY);
            textY += lineHeight;
          }
          
          // Add price if available
          if (item.price) {
            doc.setFontSize(9);
            doc.setTextColor(accentColor);
            doc.text(`${getTranslatedText('price')}: ${item.price}`, margin + imageWidth + 5, textY);
            textY += lineHeight;
          }
          break;
          
        case 'liste':
          // Add app store links if available
          if (item.appstore || item.playstore) {
            doc.setFontSize(9);
            doc.setTextColor(secondaryColor);
            
            if (item.appstore) {
              doc.text(`App Store: ${item.appstore}`, margin + imageWidth + 5, textY);
              textY += lineHeight;
            }
            
            if (item.playstore) {
              doc.text(`Google Play: ${item.playstore}`, margin + imageWidth + 5, textY);
              textY += lineHeight;
            }
          }
          break;
      }
      
      // Add description (truncated if too long)
      if (description) {
        doc.setFontSize(8);
        doc.setTextColor(textColor);
        
        // Split text to fit width and limit to 5 lines
        const maxWidth = contentWidth - (imageWidth + 5);
        const lines = doc.splitTextToSize(description, maxWidth);
        const maxLines = 5;
        const truncatedLines = lines.slice(0, maxLines);
        
        // Add ellipsis if truncated
        if (lines.length > maxLines) {
          truncatedLines[maxLines - 1] += '...';
        }
        
        doc.text(truncatedLines, margin + imageWidth + 5, textY);
        textY += truncatedLines.length * 4;
      }
      
      // Generate QR code
      let qrUrl = '';
      
      // Determine the URL for the QR code
      if (item.url) {
        // Use direct URL if available
        qrUrl = item.url;
      } else if (item.booking) {
        // Use booking URL if available
        qrUrl = item.booking;
      } else {
        // Generate app URL based on section and item
        const baseUrl = hotelConfig.meta.url;
        const sectionSlug = favorite.metadata.sectionSlug;
        const itemSlug = slugify(item.name);
        
        // For events, include date and time in URL if available
        if ((favorite.sourceType === 'event' || favorite.sourceType === 'event-api') && item.date && item.hour) {
          const dateSlug = item.date.replace(/-/g, '');
          const timeSlug = item.hour.replace(':', '');
          qrUrl = `${baseUrl}/${sectionSlug}/${slugify(item.name)}-${dateSlug}-${timeSlug}`;
        } else {
          qrUrl = `${baseUrl}/${sectionSlug}/${itemSlug}`;
        }
      }
      
      // Generate and add QR code
      try {
        const qrDataUrl = await QRCode.toDataURL(qrUrl, {
          width: 100,
          margin: 1,
          color: {
            dark: textColor,
            light: '#FFFFFF'
          }
        });
        
        // Position QR code below the image
        const qrY = yPosition + imageHeight + 2;
        doc.addImage(qrDataUrl, 'PNG', margin, qrY, qrCodeSize, qrCodeSize);
        
        // Add "Scan me" text
        doc.setFontSize(7);
        doc.setTextColor(secondaryColor);
        doc.text(getTranslatedText('scan_qr') || 'Scan me', margin + qrCodeSize/2, qrY + qrCodeSize + 3, { align: 'center' });
      } catch (error) {
        logger.error('Error generating QR code', {
          category: 'PDF',
          data: { error, url: qrUrl }
        });
      }
      
      // Add action buttons
      const buttonWidth = 40;
      const buttonX = margin + imageWidth + 5;
      let buttonY = Math.max(textY + 5, yPosition + imageHeight);
      
      // Add booking button if available
      if (item.booking) {
        buttonY += addButton(getTranslatedText('btn_booking'), item.booking, buttonX, buttonY, buttonWidth);
      }
      
      // Add website button if available
      if (item.url) {
        buttonY += addButton(getTranslatedText('btn_more_info'), item.url, buttonX, buttonY, buttonWidth);
      }
      
      // Add directions button if available
      if (item.fulladress) {
        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(item.fulladress)}`;
        buttonY += addButton(getTranslatedText('btn_itinerary'), mapsUrl, buttonX, buttonY, buttonWidth);
        
        // Add directions from hotel button
        if (config.fulladress) {
          const hotelMapsUrl = `https://www.google.com/maps/dir/${encodeURIComponent(config.fulladress)}/${encodeURIComponent(item.fulladress)}`;
          buttonY += addButton(getTranslatedText('btn_itinerary_hotel'), hotelMapsUrl, buttonX, buttonY, buttonWidth);
        }
      }
      
      // Add call button if available
      if (item.telephone) {
        const telUrl = `tel:${item.telephone.replace(/\s+/g, '')}`;
        buttonY += addButton(getTranslatedText('btn_call'), telUrl, buttonX, buttonY, buttonWidth);
      }
      
      // Add separator line between items
      if (index < favorites.length - 1) {
        const lineY = Math.max(buttonY + 5, yPosition + imageHeight + qrCodeSize + 10);
        doc.setDrawColor(accentColor);
        doc.setLineWidth(0.2);
        doc.line(margin, lineY, pageWidth - margin, lineY);
        
        // Update position for next item
        yPosition = lineY + 10;
      } else {
        // Update position for footer
        yPosition = Math.max(buttonY + 10, yPosition + imageHeight + qrCodeSize + 15);
      }
    }
    
    // Add footer with hotel information
    doc.setFontSize(8);
    doc.setTextColor(secondaryColor);
    
    // Hotel name and address
    const footerText = `${config.name} - ${config.fulladress}`;
    doc.text(footerText, margin, pageHeight - 15);
    
    // Date generated
    let generatedText;
    try {
      generatedText = `${getTranslatedText('generated_on') || 'Generated on'} ${new Date().toLocaleDateString(pdfLanguage, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    } catch (error) {
      // Fallback to English if there's an error with date formatting
      generatedText = `${getTranslatedText('generated_on') || 'Generated on'} ${new Date().toLocaleDateString('en', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    }
    
    doc.text(generatedText, pageWidth - margin, pageHeight - 15, { align: 'right' });
    
    // Add page numbers
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(secondaryColor);
      doc.text(
        `${getTranslatedText('page') || 'Page'} ${i} / ${totalPages}`, 
        pageWidth / 2, 
        pageHeight - 10, 
        { align: 'center' }
      );
    }
    
    // Generate filename
    const timestamp = new Date().toISOString().replace(/[^0-9]/g, '').slice(0, 14);
    const fileName = `${config.name.toLowerCase().replace(/\s+/g, '-')}-favoris-${timestamp}.pdf`;
    
    // Save the PDF
    doc.save(fileName);
    
    logger.debug('PDF generated successfully', {
      category: 'PDF',
      data: { 
        itemCount: favorites.length,
        fileName,
        pages: totalPages,
        language,
        usedFallbackLanguage: useFallbackLanguage,
        pdfLanguage
      }
    });
    
    return Promise.resolve();
  } catch (error) {
    logger.error('Error generating PDF', {
      category: 'PDF',
      data: { error }
    });
    throw error;
  }
}